class UniteRoomsDeviceSelectionService {
    constructor(
        $window,
        $rootScope,
        $q,
        $timeout,
        $interval,
        logService,
        subscriptionService,
        avDeviceService,
        meetingService,
        avDeviceSettingsService,
        localStreamManagerService,
        storageService
    ) {
        this.$window = $window;
        this.$rootScope = $rootScope;
        this.$q = $q;
        this.$timeout = $timeout;
        this.$interval = $interval;
        this.logger = logService.createLoggerWithSource('UniteRoomsDeviceSelectionService');
        this.rootSubscription = subscriptionService.createSubscription();
        this.avDeviceService = avDeviceService;
        this.meetingService = meetingService;
        this.avDeviceSettingsService = avDeviceSettingsService;
        this.localStreamManagerService = localStreamManagerService;
        this.storageService = storageService;
        this.audioOutputTrack = new Audio(avDeviceService.getAudioOutputTestSource());

        this.deviceGroups = {
            audioinput: [],
            audiooutput: [],
            videoinput: []
        };
        this.micLevelsMap = {};
        this.micLevelsInterval = undefined;
    }

    init() {
        this.subscribePreloadedApiEvents();
        this.subscribeBroadcastEvents();
        this.micLevelsInterval = this.startSendingMicLevelsInterval();
    }

    prepareAvDeviceInfo() {
        if (!this.$window.preloadedApi.sendAvDeviceInfo) {
            this.logger.warn(
                'prepareAvDeviceInfo',
                'Current version of unite rooms application does not support av device info.'
            );
            return;
        }

        this.avDeviceService
            .enumerateDevices('prepare-device-info')
            .catch(err => this.logger.error('prepareAvDeviceInfo', 'enumerateDevices error:', err))
            .then(() => this.populateDeviceLists())
            .catch(err => this.logger.error('prepareAvDeviceInfo', 'populateDeviceLists error:', err))
            .finally(() => {
                this.logger.log('prepareAvDeviceInfo', 'Send av device info', this.deviceGroups);
                this.$window.preloadedApi.sendAvDeviceInfo(this.deviceGroups);
            });
    }

    populateDeviceLists() {
        var deferred = this.$q.defer();

        this.clearDeviceLists();

        const micPromise = this.populateSingleDeviceList(
            this.avDeviceService.microphones,
            this.deviceGroups.audioinput
        ).catch(err => {
            this.logger.error('populateDeviceLists', 'Error populating device list - Mic', err);
        });
        const speakerPromise = this.populateSingleDeviceList(
            this.avDeviceService.speakers,
            this.deviceGroups.audiooutput
        ).catch(err => {
            this.logger.error('populateDeviceLists', 'Error populating device list - Speaker', err);
        });
        const webcamPromise = this.populateSingleDeviceList(
            this.avDeviceService.webcams,
            this.deviceGroups.videoinput
        ).catch(err => {
            this.logger.error('populateDeviceLists', 'Error populating device list - Webcam', err);
        });

        this.$q
            .all([micPromise, speakerPromise, webcamPromise])
            .catch(err => {
                //Each promise has its own catch, so this catch block shouldn't ever be reached... logging just in case
                this.logger.error('populateDeviceLists', 'Error populating device lists', err);
            })
            .finally(() => deferred.resolve());

        return deferred.promise;
    }

    populateSingleDeviceList(enumeratedList, deviceGroupList) {
        const deferred = this.$q.defer();

        if (enumeratedList.length === 0) {
            deferred.resolve();
        } else {
            //Prevents generateDeviceObj from retrieving getUserMedia stream for each device
            const noStream = true;

            enumeratedList.forEach((mediaDeviceInfo, index, arr) => {
                this.avDeviceService
                    .generateDeviceObj(mediaDeviceInfo, index + 1, noStream)
                    .then(deviceObj => {
                        const hasError =
                            deviceObj.deviceInfo.kind === 'audioinput'
                                ? !this.avDeviceService.getIsMicrophoneDeviceSwitching() &&
                                  !!this.avDeviceService.getSfuDeviceErrorFromId(deviceObj.deviceInfo.deviceId)
                                : !!this.avDeviceService.getSfuDeviceErrorFromId(deviceObj.deviceInfo.deviceId);

                        const deviceInfo = {
                            hasError,
                            deviceId: deviceObj.deviceInfo.deviceId,
                            label: deviceObj.fullDisplayName,
                            inUse: deviceObj.inUse
                        };
                        deviceGroupList.push(deviceInfo);
                    })
                    .catch(err => this.logger.log('populateSingleDeviceList', 'Unable to generate device obj:', err))
                    .finally(() => index === arr.length - 1 && deferred.resolve());
            });
        }

        return deferred.promise;
    }

    clearDeviceLists() {
        this.deviceGroups = {
            audioinput: [],
            audiooutput: [],
            videoinput: []
        };
    }

    changeActiveAvDevice(deviceKind, deviceId) {
        const deviceInfo = this.deviceGroups[deviceKind].find(x => x.deviceId === deviceId);

        if (!deviceInfo) {
            this.logger.error('changeActiveAvDevice', 'Cannot find device:', deviceId);
            return;
        }

        this.logger.log('changeActiveAvDevice', 'Change active device to:', deviceInfo.label, deviceInfo.deviceId);

        let deviceStorageName, ecdMethodName;
        switch (deviceKind) {
            case 'audioinput':
                deviceStorageName = 'audio-device';
                ecdMethodName = 'setExplicitlyChosenAudioIn';
                break;
            case 'audiooutput':
                deviceStorageName = 'audio-output-device';
                ecdMethodName = 'setExplicitlyChosenAudioOut';
                break;
            case 'videoinput':
                deviceStorageName = 'video-device';
                ecdMethodName = 'setExplicitlyChosenVideo';
                break;
            default:
        }

        if (deviceInfo.deviceId === this.storageService.get(deviceStorageName)) {
            if (deviceKind !== 'audioinput' || this.localStreamManagerService.isLocalAudioTrackActive()) {
                this.logger.log('changeActiveAvDevice', 'Device already in use... returning');
                return;
            }
        }

        this.storageService.set(deviceStorageName, deviceInfo.deviceId);

        if (this.meetingService.getIsSfuEnabled()) {
            this.avDeviceSettingsService[ecdMethodName](deviceInfo.deviceId, deviceInfo.label);
            this.avDeviceService.setDeviceErrors({ camError: false, micError: false });
        }

        this.$rootScope.$broadcast('finished-media-device-selection', {
            newVideoInputDeviceSelected: deviceKind === 'videoinput',
            newAudioInputDeviceSelected: deviceKind === 'audioinput',
            newAudioOutputDeviceSelected: deviceKind === 'audiooutput'
        });

        this.prepareAvDeviceInfo();
    }

    testAudioOutput(deviceId) {
        const deviceInfo = this.deviceGroups.audiooutput.find(x => x.deviceId === deviceId);

        if (!deviceInfo) {
            this.logger.error('testAudioOutput', 'Cannot find audio output device:', deviceId);
            return;
        }

        this.logger.log('Testing audio output for:', deviceInfo.label);

        try {
            if (!this.audioOutputTrack) {
                this.audioOutputTrack = new Audio(this.avDeviceService.getAudioOutputTestSource());
            }

            // If already playing, stop and reset
            if (!this.audioOutputTrack.paused) {
                this.audioOutputTrack.pause();
                this.audioOutputTrack.currentTime = 0;
            }

            // Set audio output
            this.audioOutputTrack.setSinkId(deviceInfo.deviceId);

            this.audioOutputTrack.play();
        } catch (e) {
            this.logger.log('Unable to test audio output:', e);
        }
    }

    testAudioInput(deviceId) {
        const deviceInfo = this.deviceGroups.audioinput.find(x => x.deviceId === deviceId);

        if (!deviceInfo) {
            this.logger.error('testAudioInput', 'Cannot find audio input device:', deviceId);
            return;
        }

        const testMicIntervalInMs = 5000;
        this.logger.log('testAudioInput', `Testing audio input in for: ${deviceInfo.label}`);

        const deferred = this.$q.defer();

        this.trackDeviceMicLevel(deviceInfo.deviceId, testMicIntervalInMs)
            .then(() => deferred.resolve())
            .catch(reason => {
                this.logger.error('testAudioInput', 'Failed to track mic level for device.', reason);
                deferred.resolve(); // it does not matter resolve or reject here
            });
    }

    trackDeviceMicLevel(deviceId, testMicIntervalInMs) {
        return this.avDeviceService
            .getStream(this.avDeviceService.generateConstraints('audio', deviceId))
            .then(stream => {
                this.micLevelsMap[deviceId] = {};
                this.micLevelsMap[deviceId]['stream'] = stream;

                try {
                    const trackIndex = this.avDeviceService.trackMicLevelForPreview(stream, newMicLevel => {
                        if (this.micLevelsMap[deviceId]) {
                            this.micLevelsMap[deviceId]['micLevel'] = newMicLevel;
                        }
                    });
                    this.micLevelsMap[deviceId]['trackIndex'] = trackIndex;
                    return this.$timeout(() => trackIndex, testMicIntervalInMs);
                } catch (e) {
                    this.logger.error('trackDeviceMicLevel', 'Failed to start mic level tracking for preview', e);
                    // just stop mic tracking immediately in next handler
                    return this.$timeout(() => -1, 0);
                }
            })
            .then(trackIndex => this.stopTrackDeviceMicLevel(deviceId, trackIndex));
    }

    stopTrackDeviceMicLevel(deviceId, trackIndex) {
        try {
            this.avDeviceService.stopTrackMicLevelForPreview(trackIndex);

            if (this.micLevelsMap[deviceId] && this.micLevelsMap[deviceId].stream) {
                if (this.micLevelsMap[deviceId].stream.stop) {
                    this.micLevelsMap[deviceId].stream.stop();
                }

                if (this.micLevelsMap[deviceId].stream.getTracks) {
                    this.micLevelsMap[deviceId].stream.getTracks().forEach(track => track.stop());
                }
            }
        } catch (e) {
            this.logger.error('trackDeviceMicLevel', 'Failed to stop mic level tracking for preview', e);
        } finally {
            delete this.micLevelsMap[deviceId];
        }
    }

    startSendingMicLevelsInterval() {
        if (!this.$window.preloadedApi.sendMicLevels) {
            this.logger.warn(
                'prepareAvDeviceInfo',
                'Current version of unite rooms application does not support av device info.'
            );
            return undefined;
        }

        return this.$interval(() => {
            const trackedDeviceIdList = Object.keys(this.micLevelsMap);

            if (!trackedDeviceIdList.length) {
                return;
            }

            const deviceIdMicLevelMap = trackedDeviceIdList.reduce((acc, deviceId) => {
                acc[deviceId] = this.micLevelsMap[deviceId]['micLevel'];
                return acc;
            }, {});

            this.$window.preloadedApi.sendMicLevels(deviceIdMicLevelMap);
        }, 250);
    }

    subscribePreloadedApiEvents() {
        if (
            !this.$window.preloadedApi.onGetAvDeviceInfo ||
            !this.$window.preloadedApi.onUseAvDevice ||
            !this.$window.preloadedApi.onTestSpeakerDevice ||
            !this.$window.preloadedApi.onTestMicDevice
        ) {
            this.logger.warn(
                'subscribePreloadedApiEvents',
                'Current version of unite rooms application does not support callback handling.'
            );
            return;
        }

        this.rootSubscription.add(
            this.$window.preloadedApi.onGetAvDeviceInfo(() => {
                this.logger.log(
                    'subscribePreloadedApiEvents',
                    'Get av device info just received for unite rooms application.'
                );
                this.prepareAvDeviceInfo();
            })
        );

        this.rootSubscription.add(
            this.$window.preloadedApi.onUseAvDevice((_, { deviceKind, deviceId }) => {
                this.logger.log(
                    'subscribePreloadedApiEvents',
                    'Selected av device just received for unite rooms application.'
                );
                this.changeActiveAvDevice(deviceKind, deviceId);
            })
        );

        this.rootSubscription.add(
            this.$window.preloadedApi.onTestSpeakerDevice((_, deviceId) => {
                this.logger.log(
                    'subscribePreloadedApiEvents',
                    'Test speaker device just received for unite rooms application.'
                );
                this.testAudioOutput(deviceId);
            })
        );

        this.rootSubscription.add(
            this.$window.preloadedApi.onTestMicDevice((_, deviceId) => {
                this.logger.log(
                    'subscribePreloadedApiEvents',
                    'Test mic device just received for unite rooms application.'
                );
                this.testAudioInput(deviceId);
            })
        );
    }

    subscribeBroadcastEvents() {
        this.$rootScope.$on('device-change-detected', () => this.prepareAvDeviceInfo());
        this.$rootScope.$on('sfu-device-errors-changed', () => this.prepareAvDeviceInfo());
        this.$rootScope.$on('$destroy', () => this.onScopeDestroyed());
    }

    onScopeDestroyed() {
        if (this.rootSubscription) {
            this.rootSubscription.unsubscribe();
        }
        if (this.micLevelsInterval) {
            this.$interval.cancel(this.micLevelsInterval);
        }
    }
}

firebird.service('uniteRoomsDeviceSelectionService', [
    '$window',
    '$rootScope',
    '$q',
    '$timeout',
    '$interval',
    'logService',
    'subscriptionService',
    'avDeviceService',
    'meetingService',
    'avDeviceSettingsService',
    'localStreamManagerService',
    'storageService',
    function (...deps) {
        return new UniteRoomsDeviceSelectionService(...deps);
    }
]);
